import React, { useState, useCallback, useEffect, useRef, Suspense, lazy } from "react";
import ScrambleText from "../animation/ScrambleText";
import MagneticText from "../animation/MagneticText";

import MorphingText from "../animation/MorphingText";
const Logo = lazy(() => import("../ui/Logo"));

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const MORPH_MS = 400;

function ScrambleTransition({ text }) {
  const [displayed, setDisplayed] = useState(text);
  const prevRef = useRef(text);
  const rafRef = useRef(null);

  useEffect(() => {
    if (text === prevRef.current) return;
    const from = prevRef.current;
    prevRef.current = text;
    const to = text;
    const maxLen = Math.max(from.length, to.length);
    const start = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - start) / MORPH_MS, 1);
      let result = '';
      for (let i = 0; i < maxLen; i++) {
        const cp = (progress * maxLen - i) / 1.5;
        const tc = i < to.length ? to[i] : '';
        const fc = i < from.length ? from[i] : '';
        if (tc === ' ' || fc === ' ') { result += tc || fc; }
        else if (cp >= 1 && tc) { result += tc; }
        else if (cp > 0) { result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]; }
        else if (fc) { result += fc; }
      }
      setDisplayed(result);
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
      else setDisplayed(to);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [text]);

  return <>{displayed}</>;
}

const EMAIL = "info@dehayez.com";

// Persist scramble state across theme-triggered remounts
let _scrambleComplete = false;

export default function Content () {
    const [scrambleComplete, setScrambleComplete] = useState(_scrambleComplete);
    const [copied, setCopied] = useState(false);

    const handleScrambleComplete = useCallback(() => {
      _scrambleComplete = true;
      setScrambleComplete(true);
    }, []);

    const handleEmailClick = useCallback((e) => {
      // Try mailto first — on mobile, if the user has a default mail app configured
      // it will open. If they want to copy instead, they can long-press.
      // On desktop, add a fallback: copy to clipboard if mailto doesn't trigger.
      const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (!isMobile) {
        e.preventDefault();
        navigator.clipboard.writeText(EMAIL).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3500);
        });

      }
    }, []);

    return (
      <main className="content">
        <div className="content__logo" role="img" aria-label="Dehayez logo">
          <Suspense fallback={<div className='list-item__logo list-item__logo--3d' style={{ background: 'transparent' }}><img src="/favicon.svg" alt="Dehayez" style={{ width: '42%', height: '42%', objectFit: 'contain', display: 'block', margin: 'auto', opacity: 1 }} /></div>}>
            <Logo/>
          </Suspense>
        </div>
        <h1 className="content__tagline content__tagline--entrance">
          {!scrambleComplete ? (
            <ScrambleText
              text="Creative from Belgium"
              delay={700}
              duration={800}
              onComplete={handleScrambleComplete}
            />
          ) : (
            <><MagneticText text="Creative" delay={0} className="content__tagline-creative" />{" from Belgium"}</>
          )}
        </h1>
        <p className="content__subtitle content__subtitle--entrance">
          <MorphingText
            texts={[
              "Music · Development · Design · Photography",
              "Beats · Code · Visuals · Captures",
              "Sound · Systems · Aesthetics · Moments",
            ]}
            delay={1200}
          />
        </p>
        <a
          className={`content__cta content__cta--entrance${copied ? ' content__cta--copied' : ''}`}
          href={`mailto:${EMAIL}`}
          onClick={handleEmailClick}
          title={EMAIL}
          aria-label={`Contact via email: ${EMAIL}`}
        >
          <span><ScrambleTransition text={copied ? "Email copied!" : "Get in touch"} /></span> <span className="content__cta-arrow">&rarr;</span>
        </a>
      </main>
    )
}
